/**
 * RepairPhotoGallery — the landlord Repair Detail Photos card (RA-ATT-LANDLORD-READ,
 * RA-ATT-ACTIVITY). Read-only: omitted entirely when there are no photos; thumbnails open the
 * full-size viewer. Each caption says when the photo arrived — at submission, or added later.
 */

import { useRef, useState } from 'react';
import { asIsoDateTimeString, formatTimestamp } from '@resida/shared';
import type { RepairAttachment } from '@resida/api-types/repair';
import { RepairPhotoViewer, type RepairPhotoViewerItem } from './RepairPhotoViewer';

/** Photos attached in the create transaction carry (near-)identical timestamps to the case row. */
const AT_SUBMISSION_WINDOW_MS = 2_000;

const CAPTION_DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

interface RepairPhotoGalleryProps {
  attachments: RepairAttachment[];
  /** Case.createdAt — attachments created after it (plus a short window) were added later. */
  caseCreatedAt: string;
  timezone: string;
  /** The attachment list failed to load; the card says so instead of vanishing. */
  loadFailed?: boolean;
  onRetry?: () => void;
}

export function RepairPhotoGallery({
  attachments,
  caseCreatedAt,
  timezone,
  loadFailed = false,
  onRetry,
}: RepairPhotoGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const thumbRefs = useRef(new Map<string, HTMLButtonElement>());
  const openedIdRef = useRef<string | null>(null);

  if (attachments.length === 0 && loadFailed) {
    return (
      <div className="px-[var(--page-padding-x)] mt-4">
        <section
          aria-labelledby="repair-photos-heading"
          data-testid="repair-photos"
          className="rounded-xl border border-border bg-card p-4 md:p-6"
        >
          <h2 id="repair-photos-heading" className="m-0 mb-3 text-base font-semibold text-foreground">
            Photos
          </h2>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p role="alert" data-testid="repair-photos-error" className="m-0 text-sm text-destructive">
              Couldn’t load the tenant’s photos.
            </p>
            <button
              type="button"
              data-testid="repair-photos-retry"
              onClick={onRetry}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-input px-3.5 text-sm text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Try again
            </button>
          </div>
        </section>
      </div>
    );
  }
  if (attachments.length === 0) return null;

  const caseCreatedMs = Date.parse(caseCreatedAt);
  const photos = attachments.map((attachment) => {
    const addedLater = Date.parse(attachment.createdAt) - caseCreatedMs > AT_SUBMISSION_WINDOW_MS;
    const date = formatTimestamp(asIsoDateTimeString(attachment.createdAt), timezone, CAPTION_DATE);
    return { attachment, addedLater, caption: `${date} · ${addedLater ? 'added later' : 'at submission'}` };
  });
  const viewerItems: RepairPhotoViewerItem[] = photos.map(({ attachment, caption }) => ({
    id: attachment.id,
    url: attachment.url,
    caption,
  }));
  // A live update can shrink the list under an open viewer.
  const viewerIndex = openIndex !== null && openIndex < photos.length ? openIndex : null;

  return (
    <div className="px-[var(--page-padding-x)] mt-4">
      <section
        aria-labelledby="repair-photos-heading"
        data-testid="repair-photos"
        className="rounded-xl border border-border bg-card p-4 md:p-6"
      >
        <div className="mb-4 flex items-baseline justify-between gap-3">
          <h2 id="repair-photos-heading" className="m-0 text-base font-semibold text-foreground">
            Photos{' '}
            <span className="font-normal text-muted-foreground">· {photos.length} from the tenant</span>
          </h2>
          <span className="hidden text-xs text-muted-foreground sm:inline">
            Select a photo to view it full size
          </span>
        </div>
        <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 md:grid-cols-3 xl:grid-cols-5">
          {photos.map(({ attachment, addedLater, caption }, i) => (
            <li key={attachment.id} className="m-0 flex flex-col gap-1.5">
              <button
                type="button"
                data-testid={`repair-photo-${attachment.id}`}
                aria-label={`View photo ${i + 1} full size${addedLater ? ', added after submission' : ''}`}
                ref={(el) => {
                  if (el) thumbRefs.current.set(attachment.id, el);
                  else thumbRefs.current.delete(attachment.id);
                }}
                onClick={() => {
                  openedIdRef.current = attachment.id;
                  setOpenIndex(i);
                }}
                className="aspect-[4/3] w-full cursor-pointer overflow-hidden rounded-lg border border-border bg-muted p-0 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <img src={attachment.url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
              </button>
              <span className="text-xs text-muted-foreground">{caption}</span>
            </li>
          ))}
        </ul>
      </section>
      <RepairPhotoViewer
        items={viewerItems}
        index={viewerIndex}
        onIndexChange={setOpenIndex}
        onClose={() => setOpenIndex(null)}
        onReturnFocus={() => {
          const id = openedIdRef.current;
          if (id) thumbRefs.current.get(id)?.focus();
        }}
      />
    </div>
  );
}
